import { getWsToken } from './agents';

const CLOUDFLARE_WORKER_URL = 'https://odoo-ai-agent.cooperdownthehatch.workers.dev';

export interface KnowledgeStatus {
  indexed: boolean;
  indexedAt?: string;
  stats?: {
    modelsIndexed?: number;
    fieldsIndexed?: number;
    patternsIndexed?: number;
    terminologyIndexed?: number;
    codeChunksIndexed?: number;
  };
}

export interface IndexResult {
  success: boolean;
  message?: string;
  error?: string;
  modelsIndexed?: number;
  fieldsIndexed?: number;
  patternsIndexed?: number;
  terminologyIndexed?: number;
  codeChunksIndexed?: number;
}

/**
 * Get knowledge base indexing status for a project
 */
export async function getKnowledgeStatus(projectId: string, buildId?: number): Promise<KnowledgeStatus> {
  let headers: Record<string, string> = {};

  // Try to get auth token if buildId is provided
  if (buildId) {
    try {
      const { token } = await getWsToken(projectId, buildId);
      headers = { Authorization: `Bearer ${token}` };
    } catch {
      // Continue without auth
    }
  }

  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/api/projects/${projectId}/knowledge/status`, {
    headers,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch knowledge status');
  }

  return response.json();
}

/**
 * Trigger knowledge base indexing for a project
 */
export async function indexKnowledgeBase(
  projectId: string,
  buildId: number,
  options?: {
    force?: boolean;
    includeCode?: boolean;
    modulePaths?: string[];
  }
): Promise<IndexResult> {
  // Get auth token for the Cloudflare worker
  const { token } = await getWsToken(projectId, buildId);

  const response = await fetch(
    `${CLOUDFLARE_WORKER_URL}/api/projects/${projectId}/builds/${buildId}/knowledge/index`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        force: options?.force ?? false,
        includeCode: options?.includeCode ?? false,
        modulePaths: options?.modulePaths,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' })) as { error?: string; details?: string };
    const message = error.details || error.error || `HTTP ${response.status}`;
    throw new Error(`Failed to index knowledge base: ${message}`);
  }

  return response.json();
}

export interface KnowledgeSearchResult {
  id: string;
  score: number;
  type: 'model' | 'field' | 'pattern' | 'terminology' | 'code';
  content: string;
  metadata: {
    model_name?: string;
    field_name?: string;
    field_type?: string;
    field_label?: string | Record<string, string>; // Can be string or translation object
    relation?: string;
    required?: boolean;
    source?: string;
    pattern_sql?: string;
    projectId?: string;
  };
}

export interface KnowledgeSearchResponse {
  query: string;
  results: KnowledgeSearchResult[];
  totalVectors?: number;
}

/**
 * Search the knowledge base
 */
export async function searchKnowledge(
  projectId: string,
  query: string,
  limit: number = 20
): Promise<KnowledgeSearchResponse> {
  const response = await fetch(
    `${CLOUDFLARE_WORKER_URL}/debug/search/${projectId}?q=${encodeURIComponent(query)}&limit=${limit}`
  );

  if (!response.ok) {
    throw new Error('Failed to search knowledge base');
  }

  return response.json();
}

export interface KnowledgeOverview {
  vectorCount: number;
  uniqueProjectIds: string[];
  samples: Array<{
    id: string;
    score: number;
    projectId: string;
    type: string;
    model?: string;
  }>;
}

/**
 * Get knowledge base overview (debug info)
 */
export async function getKnowledgeOverview(): Promise<KnowledgeOverview> {
  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/debug/vectors`);

  if (!response.ok) {
    throw new Error('Failed to fetch knowledge overview');
  }

  return response.json();
}
